import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import StatusIndicator from '../../../components/ui/StatusIndicator';

const ExportTable = ({ 
  exports, 
  selectedExports, 
  onSelectionChange, 
  onDownload, 
  onRegenerate, 
  onPreview,
  sortConfig,
  onSort 
}) => {
  const [hoveredRow, setHoveredRow] = useState(null);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpired = (expirationDate) => {
    return new Date(expirationDate) < new Date();
  };

  const getExpirationStatus = (expirationDate) => {
    const now = new Date();
    const expiry = new Date(expirationDate);
    const hoursUntilExpiry = (expiry - now) / (1000 * 60 * 60);

    if (hoursUntilExpiry < 0) return 'expired';
    if (hoursUntilExpiry < 24) return 'expiring-soon';
    return 'active';
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange(exports.map(exp => exp?.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectRow = (exportId, checked) => {
    if (checked) {
      onSelectionChange([...selectedExports, exportId]);
    } else {
      onSelectionChange(selectedExports?.filter(id => id !== exportId));
    }
  };

  const getSortIcon = (column) => {
    if (sortConfig?.key !== column) return 'ArrowUpDown';
    return sortConfig?.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  const allSelected = exports.length > 0 && selectedExports?.length === exports.length;
  const someSelected = selectedExports?.length > 0 && selectedExports?.length < exports.length;

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/30 border-b border-border">
            <tr>
              <th className="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={input => {
                    if (input) input.indeterminate = someSelected;
                  }}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                  className="rounded border-border focus:ring-2 focus:ring-primary"
                />
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => onSort('filename')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary transition-smooth"
                >
                  <span>File Name</span>
                  <Icon name={getSortIcon('filename')} size={14} />
                </button>
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => onSort('exportDate')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary transition-smooth"
                >
                  <span>Export Date</span>
                  <Icon name={getSortIcon('exportDate')} size={14} />
                </button>
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => onSort('type')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary transition-smooth"
                >
                  <span>Type</span>
                  <Icon name={getSortIcon('type')} size={14} />
                </button>
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => onSort('format')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary transition-smooth"
                >
                  <span>Format</span>
                  <Icon name={getSortIcon('format')} size={14} />
                </button>
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => onSort('invoiceCount')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary transition-smooth"
                >
                  <span>Invoices</span>
                  <Icon name={getSortIcon('invoiceCount')} size={14} />
                </button>
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => onSort('fileSize')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary transition-smooth"
                >
                  <span>Size</span>
                  <Icon name={getSortIcon('fileSize')} size={14} />
                </button>
              </th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Expires</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {exports.map((exportItem) => {
              const isSelected = selectedExports?.includes(exportItem?.id);
              const expirationStatus = getExpirationStatus(exportItem?.expirationDate);
              
              return (
                <tr
                  key={exportItem?.id}
                  className={`
                    border-b border-border transition-smooth
                    ${isSelected ? 'bg-primary/5' : 'hover:bg-muted/30'}
                  `}
                  onMouseEnter={() => setHoveredRow(exportItem?.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => handleSelectRow(exportItem?.id, e?.target?.checked)}
                      className="rounded border-border focus:ring-2 focus:ring-primary"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <Icon 
                        name={exportItem?.format === 'CSV' ? 'FileText' : 'FileSpreadsheet'} 
                        size={16} 
                        className="text-muted-foreground" 
                      />
                      <span className="text-sm font-medium text-foreground">
                        {exportItem?.filename}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-foreground">
                      {formatDate(exportItem?.exportDate)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-foreground">{exportItem?.type}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-mono text-foreground">{exportItem?.format}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-foreground">{exportItem?.invoiceCount}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-muted-foreground">
                      {formatFileSize(exportItem?.fileSize)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusIndicator
                      status={exportItem?.status}
                      size="sm"
                      variant="dot"
                      confidence={1.0}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <span className={`
                        text-sm
                        ${expirationStatus === 'expired' ? 'text-error' : 
                          expirationStatus === 'expiring-soon' ? 'text-warning' : 'text-muted-foreground'}
                      `}>
                        {formatDate(exportItem?.expirationDate)}
                      </span>
                      {expirationStatus === 'expiring-soon' && (
                        <Icon name="Clock" size={14} className="text-warning" />
                      )}
                      {expirationStatus === 'expired' && (
                        <Icon name="AlertTriangle" size={14} className="text-error" />
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end space-x-1">
                      {exportItem?.status === 'completed' && !isExpired(exportItem?.expirationDate) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDownload(exportItem)}
                          iconName="Download"
                          className="h-8 w-8 p-0"
                        />
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onPreview(exportItem)}
                        iconName="Eye"
                        className="h-8 w-8 p-0"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onRegenerate(exportItem)}
                        iconName="RefreshCw"
                        className="h-8 w-8 p-0"
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {exports.length === 0 && (
        <div className="text-center py-12">
          <Icon name="FileX" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No exports found</h3>
          <p className="text-sm text-muted-foreground">
            No export files match your current filters. Try adjusting your search criteria.
          </p>
        </div>
      )}
    </div>
  );
};

export default ExportTable;